import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowDown, ArrowUp, ChevronDown, Search } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@stockline/ui/components/popover";
import { Input } from "@stockline/ui/components/input";
import { Checkbox } from "@stockline/ui/components/checkbox";
import { cn } from "@/lib/utils";
import type { SortDirection } from "./useSheetFilterSort";

interface ExcelColumnHeaderMenuProps {
  readonly colIndex: number;
  /** Column letter (A, B, …), shown as the popover title. */
  readonly columnLabel: string;
  readonly sortDirection: SortDirection | null;
  readonly onSort: (colIndex: number, direction: SortDirection | null) => void;
  /** Distinct display values available in this column (data rows only). */
  readonly values: string[];
  /** Currently-selected values, or null when the column is unfiltered. */
  readonly selected: Set<string> | null;
  readonly onFilterChange: (
    colIndex: number,
    selected: Set<string> | null,
  ) => void;
  readonly isFiltered: boolean;
}

/**
 * Excel-like column dropdown: sort ascending/descending (+ clear) and a
 * searchable checkbox list of the column's distinct values. Rendering, sorting
 * and filtering are owned by {@link useSheetFilterSort}; this is the trigger +
 * popover UI, styled to match the shared stock/dictionary column popovers.
 */
export function ExcelColumnHeaderMenu({
  colIndex,
  columnLabel,
  sortDirection,
  onSort,
  values,
  selected,
  onFilterChange,
  isFiltered,
}: ExcelColumnHeaderMenuProps) {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");

  // Everything is selected when there is no explicit filter set.
  const isChecked = (v: string) => (selected ? selected.has(v) : true);

  const displayedValues = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return values;
    return values.filter((v) =>
      (v || t("documents.viewer.blank")).toLowerCase().includes(q),
    );
  }, [values, search, t]);

  // "(Select all)" reflects and acts on the CURRENTLY DISPLAYED values only, so
  // with an active search it toggles just the matches (Excel behaviour) instead
  // of silently wiping values hidden by the query.
  const displayedChecked = displayedValues.filter(isChecked);
  const allChecked =
    displayedValues.length > 0 &&
    displayedChecked.length === displayedValues.length;
  const someChecked = displayedChecked.length > 0 && !allChecked;

  const toggleValue = (v: string) => {
    // Materialize the current selection (all values when unfiltered), flip one,
    // then collapse back to null when everything ends up selected again.
    const base = selected ? new Set(selected) : new Set(values);
    if (base.has(v)) base.delete(v);
    else base.add(v);
    if (base.size === values.length) onFilterChange(colIndex, null);
    else onFilterChange(colIndex, base);
  };

  const toggleAll = () => {
    // Materialize, then add/remove only the displayed (search-matched) values.
    const base = selected ? new Set(selected) : new Set(values);
    if (allChecked) for (const v of displayedValues) base.delete(v);
    else for (const v of displayedValues) base.add(v);
    if (base.size === values.length) onFilterChange(colIndex, null);
    else onFilterChange(colIndex, base);
  };

  const isActive = isFiltered || sortDirection !== null;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={`excel-col-menu-${colIndex}`}
          // Stop the header cell's mousedown from starting a column selection,
          // but leave the click to Radix so the popover actually toggles
          // (calling preventDefault here would swallow Radix's open handler).
          onMouseDown={(e) => e.stopPropagation()}
          title={t("documents.viewer.filterSortColumn")}
          className={cn(
            "absolute right-2 top-1/2 -translate-y-1/2 z-30 inline-flex h-4 w-4 items-center justify-center rounded",
            isActive
              ? "bg-blue-100 text-blue-600 opacity-100 ring-1 ring-blue-200"
              : "bg-white/80 text-gray-400 opacity-0 hover:bg-gray-200 hover:text-gray-700 group-hover:opacity-100",
          )}
        >
          {sortDirection === "asc" ? (
            <ArrowUp className="h-3 w-3" />
          ) : sortDirection === "desc" ? (
            <ArrowDown className="h-3 w-3" />
          ) : (
            <ChevronDown className="h-3 w-3" />
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-56 p-1.5"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="px-2 py-1 text-xs font-semibold text-gray-700 truncate">
          {columnLabel}
        </div>
        <div className="h-px bg-gray-100 my-1" />

        <div className="space-y-0.5 mb-1">
          <button
            type="button"
            className={cn(
              "w-full flex items-center gap-2 px-2 py-1.5 text-xs rounded transition-colors",
              sortDirection === "asc"
                ? "bg-blue-50 text-blue-700 font-medium"
                : "text-gray-600 hover:bg-gray-50",
            )}
            onClick={() =>
              onSort(colIndex, sortDirection === "asc" ? null : "asc")
            }
          >
            <ArrowUp className="w-3 h-3" />
            {t("documents.viewer.sortAscending")}
          </button>
          <button
            type="button"
            className={cn(
              "w-full flex items-center gap-2 px-2 py-1.5 text-xs rounded transition-colors",
              sortDirection === "desc"
                ? "bg-blue-50 text-blue-700 font-medium"
                : "text-gray-600 hover:bg-gray-50",
            )}
            onClick={() =>
              onSort(colIndex, sortDirection === "desc" ? null : "desc")
            }
          >
            <ArrowDown className="w-3 h-3" />
            {t("documents.viewer.sortDescending")}
          </button>
        </div>

        <div className="h-px bg-gray-100 my-1" />

        <div className="relative mb-1.5">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("common.search", { defaultValue: "Search..." })}
            className="h-7 pl-7 text-xs"
          />
        </div>

        <label className="flex items-center gap-2 px-1.5 py-1 text-xs font-medium rounded hover:bg-muted/50 cursor-pointer">
          <Checkbox
            checked={allChecked ? true : someChecked ? "indeterminate" : false}
            onCheckedChange={toggleAll}
            className="h-3.5 w-3.5"
          />
          <span className="truncate">
            {t("documents.viewer.selectAllValues")}
          </span>
        </label>

        <div className="overflow-auto space-y-0.5 max-h-56">
          {displayedValues.map((v) => (
            <label
              key={v}
              data-testid={`excel-col-value-${colIndex}`}
              className="flex items-center gap-2 px-1.5 py-1 text-xs rounded hover:bg-muted/50 cursor-pointer"
            >
              <Checkbox
                checked={isChecked(v)}
                onCheckedChange={() => toggleValue(v)}
                className="h-3.5 w-3.5"
              />
              <span className="truncate">
                {v || (
                  <span className="italic text-gray-400">
                    {t("documents.viewer.blank")}
                  </span>
                )}
              </span>
            </label>
          ))}
          {displayedValues.length === 0 && (
            <p className="text-xs text-muted-foreground text-center py-2">
              {t("common.noResults", { defaultValue: "No results" })}
            </p>
          )}
        </div>

        {isFiltered && (
          <>
            <div className="h-px bg-gray-100 my-1.5" />
            <button
              type="button"
              className="w-full text-xs text-blue-600 hover:text-blue-800 py-1"
              onClick={() => onFilterChange(colIndex, null)}
            >
              {t("common.clearFilter", { defaultValue: "Clear filter" })}
            </button>
          </>
        )}
      </PopoverContent>
    </Popover>
  );
}
